import { ElementPropertyName } from '@shared/models/element-property.model';
import {
  IElement,
  ProductElementOverrideTarget,
} from '@shared/models/project.model';
import { getSelectedElementQuantityProperty } from './element-quantity-core.helpers';
import { getQuantityDisplayUnitForEdit } from './element-quantity.helpers';
import { getSbefCodeFromElementOrRecipe } from './sbef.helpers';
import { isProductElement } from './recursive-element.helpers';

/** Import-set fields that {@link materializeCandidate} may write on an element. */
export type ImportFields = {
  productId?: string;
  name?: string;
  quantityResolved?: number;
  quantityUnit?: string;
  sbefCode?: string;
  wastePercent?: number;
};

type ExtractImportFieldsOptions = {
  /** Fallback when the baseline element has no product child yet. */
  expectedProductId?: string;
};

/**
 * Read the subset of an element's state that CSV import controls. Only fields
 * with values are returned so callers can compare baseline vs live on a
 * per-field basis.
 */
export const extractImportFields = (
  element: IElement,
  options: ExtractImportFieldsOptions = {},
): ImportFields => {
  const productChild = element.elements.find(isProductElement);
  const productId =
    productChild?.generic_product_id ??
    productChild?.product_id ??
    options.expectedProductId;

  const selectedProp = getSelectedElementQuantityProperty(element);
  const quantityResolved =
    selectedProp?.count?.resolved ?? selectedProp?.count?.value;
  const quantityUnit =
    selectedProp?.unit ??
    getQuantityDisplayUnitForEdit(element, selectedProp?.name);

  const sbefCode = getSbefCodeFromElementOrRecipe(element);

  const wasteOverride =
    productChild?.productOverrides?.[
      ProductElementOverrideTarget.WASTE_PERCENT
    ];
  const wastePercent =
    wasteOverride?.override.type === 'replace'
      ? wasteOverride.override.value
      : undefined;

  const fields: ImportFields = {};
  if (productId) fields.productId = productId;
  if (element.name) fields.name = element.name;
  if (quantityResolved !== undefined && Number.isFinite(quantityResolved)) {
    fields.quantityResolved = quantityResolved;
  }
  if (quantityUnit) fields.quantityUnit = quantityUnit;
  if (sbefCode) fields.sbefCode = sbefCode;
  if (wastePercent !== undefined && Number.isFinite(wastePercent)) {
    fields.wastePercent = wastePercent;
  }
  return fields;
};

const IMPORT_FIELD_KEYS: (keyof ImportFields)[] = [
  'productId',
  'name',
  'quantityResolved',
  'quantityUnit',
  'sbefCode',
  'wastePercent',
];

/**
 * True when the live element differs from the import baseline on any field the
 * baseline set. Non-import edits (e.g. transport) are ignored.
 */
export const isCandidateRowEdited = (
  liveElement: IElement,
  baselineElement: IElement,
  expectedProductId?: string,
): boolean => {
  const baseline = extractImportFields(baselineElement, { expectedProductId });
  const live = extractImportFields(liveElement, { expectedProductId });

  for (const key of IMPORT_FIELD_KEYS) {
    if (baseline[key] === undefined) continue;
    if (baseline[key] !== live[key]) return true;
  }
  return false;
};

/** @internal for tests — property name used for SBEF on elements. */
export const SBEF_IMPORT_PROPERTY = ElementPropertyName.SbefCode;
